//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import React from 'react';
import styled, { css } from 'styled-components';

import { HelpIconWithMargin } from '@components/atoms/help-icon/HelpIcon';
import Icon from '@components/atoms/icon/Icon';
import { CaptionText } from '@components/atoms/text/Text';
import { useUniqueId } from '@hooks/useUniqueId';
import { TextMixin } from '@theme/mixins';
import { isDefined, isTruthy } from '@utils/filterPredicates';

export interface IDescriptionAndValidationProps {
  /**
   * A custom label element that will be rendered instead of the standard text + help icon.
   * This label element should include an `htmlFor` attribute with the given `formComponentId`.
   */
  customLabel?: React.ReactElement;
  /**
   * Error text that will appear in red on the bottom right (or both sides) of the ButtonPopover. Will
   * only appear if the `hasError` prop is also true.
   */
  errorText?: string | [string, string];
  /**
   * The id of the form component that should be focused if the label is clicked.
   */
  formComponentId?: string;
  /**
   * The flag to indicate whether we should show error UI (red styling and text) on the ButtonPopover.
   */
  hasError?: boolean;
  /**
   * Help text that will appear in gray on the bottom right of the ButtonPopover unless error text is currently showing.
   */
  helpText?: string;
  /**
   * A text label that will appear on the top left of the TextInput, and can be clicked to focus the input.
   */
  label?: React.ReactNode; // TODO DEV-60534: Require a label in all form elements
  labelTextMixin?: TextMixin;
  /**
   * The contents of the HelpIcon tooltip that will appear next to the label if this prop is provided.
   * NOTE: This text is not currently accessible to screen-readers (DEV-61290).
   */
  labelHelpText?: string;
}

interface Props extends IDescriptionAndValidationProps {
  className?: string;
  children?: React.ReactNode;
  /**
   * This render method should return a component with the following attributes:
   * - aria-describedby={descriptionTextId}
   * - aria-invalid={hasError}
   * - id={formComponentId}
   */
  renderFormComponent: (descriptionTextId?: string) => React.ReactElement;
}

/**
 * The DescriptiveAndValidationWrapper adds optional error text, help text, and label UI
 * around a basic form component such as a text input. This component is designed to add
 * unified contextual UI to other atomic form components (like the ButtonPopover) and
 * should not be used outside of the atomic design ecosystem.
 */
const DescriptionAndValidationWrapper = ({
  children,
  className = '',
  customLabel,
  errorText,
  formComponentId,
  hasError = false,
  helpText,
  label,
  labelHelpText,
  labelTextMixin,
  renderFormComponent,
}: Props) => {
  const textId = useUniqueId('form-component-text-id');

  const renderLabel = () =>
    label ? (
      <LabelWrapper>
        <Label htmlFor={formComponentId} mixin={labelTextMixin}>
          {label}
        </Label>
        {labelHelpText && <HelpIconWithMargin helpText={labelHelpText} />}
      </LabelWrapper>
    ) : undefined;

  const renderFooter = () => {
    if (hasError && Array.isArray(errorText)) {
      return (
        <Footer aria-live='assertive' hasError={true} id={textId}>
          <ErrorWrapper>
            {isTruthy(errorText[0]) && <ErrorIcon name='error' />}
            {errorText[0]}
          </ErrorWrapper>
          <ErrorWrapper>
            {!isTruthy(errorText[0]) && isTruthy(errorText[1]) && (
              <ErrorIcon name='error' />
            )}
            {errorText[1]}
          </ErrorWrapper>
        </Footer>
      );
    } else if (hasError && errorText) {
      return (
        <Footer aria-live='assertive' hasError={true} id={textId}>
          <ErrorWrapper>
            {isTruthy(errorText) && <ErrorIcon name='error' />}
            {errorText}
          </ErrorWrapper>
        </Footer>
      );
    } else if (helpText) {
      return (
        <Footer aria-live='polite' hasError={false} id={textId}>
          {helpText}
        </Footer>
      );
    }
  };

  /**
   * We only want to attach an `aria-describedby` attribute to the
   * underlying form component if we have descriptive text UI.
   */
  const descriptionTextId = helpText || errorText ? textId : undefined;

  return (
    <Wrapper className={className}>
      {customLabel ?? renderLabel()}
      {renderFormComponent(descriptionTextId)}
      {children}
      {renderFooter()}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  position: relative;
`;

const LabelWrapper = styled.div(
  ({ theme }) => `
  display: flex;
  align-items: center;
  margin-bottom: ${theme.spacings[1]};
`,
);

const Label = styled.label<{
  mixin: IDescriptionAndValidationProps['labelTextMixin'];
}>(
  ({ mixin }) => css`
    ${isDefined(mixin) ? mixin : ''};
    margin-bottom: 0; /* to make even with help icon */
  `,
);

const Footer = styled(CaptionText)<{ hasError?: boolean }>(
  ({ children, hasError = false, theme }) => `
  width: 100%;
  display: flex;
  justify-content: ${
    React.Children.count(children) > 1 ? 'space-between' : 'flex-end'
  };
  margin-top: ${theme.spacings[1]};
  color: ${hasError ? theme.colors.aliases.error : theme.colors.gray8};
`,
);

const ErrorWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const ErrorIcon = styled(Icon)(
  ({ theme }) => `
  width: 1em;
  margin-right: ${theme.spacings[1]};
`,
);

export default DescriptionAndValidationWrapper;
